"use client";

import React from "react";
import Image from "next/image";
import { ImageIcon, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import AgentPrimaryIcon from "../AgentPrimaryIcon";

export default function MessageImageAttachment({
  imageUrls,
  activeImageIndex,
  hasText,
  content,
  onImageClick,
  onImageLoad,
  onSelectImage,
  variant = "render",
}: {
  imageUrls: string[];
  activeImageIndex: number;
  hasText: boolean;
  content: string;
  onImageClick: (imageIndex?: number) => void;
  onImageLoad?: (imageUrl: string, imageIndex: number) => void;
  onSelectImage: (index: number) => void;
  variant?: "reference" | "render";
}) {
  const activeImageUrl = imageUrls[activeImageIndex];

  if (variant === "reference") {
    return (
      <div
        data-reference-image-attachment
        className={hasText ? "mt-3 space-y-2" : "space-y-2"}
      >
        <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-chat-bubble-user-foreground/75">
          <ImageIcon className="size-3.5" aria-hidden="true" />
          <span>
            {imageUrls.length === 1
              ? "Reference image"
              : `${imageUrls.length} reference images`}
          </span>
        </div>
        <div
          className={
            imageUrls.length === 1
              ? "grid w-48 grid-cols-1 sm:w-64"
              : "grid w-56 grid-cols-2 gap-1.5 sm:w-72"
          }
        >
          {imageUrls.map((url, index) => (
            <Button
              key={`${url}-${index}`}
              type="button"
              variant="image-thumbnail"
              onClick={() => {
                onSelectImage(index);
                onImageClick(index);
              }}
              aria-label={`Open reference image ${index + 1} of ${imageUrls.length}`}
              className="group relative aspect-[4/3] h-auto w-full cursor-zoom-in touch-manipulation overflow-hidden rounded-xl border border-white/20 p-0 shadow-sm"
            >
              <Image
                src={url}
                alt={`Reference image ${index + 1} of ${imageUrls.length}`}
                fill
                sizes={
                  imageUrls.length === 1
                    ? "(max-width: 640px) 192px, 256px"
                    : "(max-width: 640px) 112px, 144px"
                }
                quality={75}
                className="object-cover transition-transform duration-300 group-hover:scale-[1.03]"
              />
              <span className="absolute inset-0 flex items-center justify-center bg-black/0 transition-colors group-hover:bg-black/10">
                <span className="rounded-full bg-background/90 p-2 text-foreground opacity-0 shadow-sm transition-opacity group-hover:opacity-100">
                  <Search className="size-4" aria-hidden="true" />
                </span>
              </span>
            </Button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Main thumbnail - clickable */}
      <Button
        type="button"
        variant="image-thumbnail"
        onClick={() => onImageClick()}
        aria-label="Open image attachment"
        className="group relative mt-2 aspect-[2/3] h-auto w-28 cursor-pointer touch-manipulation overflow-hidden rounded-lg p-0 transition-all duration-200 sm:w-40"
      >
        <Image
          src={activeImageUrl}
          alt={hasText ? `Image: ${content}` : "Image attachment"}
          fill
          sizes="(max-width: 640px) 128px, 160px"
          quality={75}
          className="object-cover transition-transform duration-300 group-hover:scale-105"
          onLoad={() => onImageLoad?.(activeImageUrl, activeImageIndex)}
        />
        {/* Click indicator overlay */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="opacity-0 group-hover:opacity-100 transition-opacity rounded-full p-2">
            <AgentPrimaryIcon>
              <Search className="size-4" />
            </AgentPrimaryIcon>
          </div>
        </div>
      </Button>

      {/* Variation thumbnails */}
      {imageUrls.length > 1 && (
        <div className="flex flex-wrap gap-1.5 justify-start">
          {imageUrls.map((url, idx) => (
            <Button
              key={`${url}-${idx}`}
              type="button"
              variant="image-variation"
              onClick={() => onSelectImage(idx)}
              data-selected={activeImageIndex === idx ? "true" : undefined}
              className="relative w-10 h-12 sm:w-12 sm:h-16 rounded-md overflow-hidden p-0"
            >
              <Image
                src={url}
                alt={`Variation ${idx + 1}`}
                fill
                sizes="48px"
                quality={75}
                className="object-cover"
                onLoad={() => onImageLoad?.(url, idx)}
              />
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}
